import api from '@/api';
import type { GetSessionResponse, SessionTabData } from '@/types/sessions.types';
import {
  isWebExtensionAccountMismatch,
  resolveWebAidForExtension,
  type SendCBMessage,
} from './extensionSessions';

export function parseGetSessionPayload(raw: unknown): GetSessionResponse {
  const rawObj = raw as GetSessionResponse;
  return rawObj?.data ?? rawObj;
}

export type SessionTabsLoadResult = {
  tabId: string[];
  tabOrgId: (string | number)[];
  tabData: Record<string, SessionTabData>;
  isWorkSpace: boolean;
  accountMismatch: boolean;
  sleepingTabCount?: number;
};

export async function fetchSessionTabsFromApi(
  sessionId: string
): Promise<Omit<SessionTabsLoadResult, 'accountMismatch'>> {
  const response = await api.get<{ status?: string; tabs?: Record<string, SessionTabData> }>(
    `/sessions/tabs?id=${encodeURIComponent(sessionId)}`
  );
  const data = response.data;
  const tabs = data?.tabs && typeof data.tabs === 'object' ? data.tabs : {};
  const tabId = Object.keys(tabs);
  return {
    tabId,
    tabOrgId: [],
    tabData: tabs,
    isWorkSpace: true,
  };
}

/**
 * Load session tabs via extension getSession (passes web aid for SW verification).
 * On account mismatch, falls back to GET /sessions/tabs for the web workspace only.
 */
export async function loadSessionTabsViaExtensionOrApi(
  sendCBMessage: SendCBMessage,
  sessionId: string,
  webAid?: string
): Promise<SessionTabsLoadResult> {
  const aid = webAid ?? resolveWebAidForExtension();

  return new Promise<SessionTabsLoadResult>((resolve, reject) => {
    sendCBMessage(
      'getSession',
      { sid: sessionId, ...(aid ? { aid } : {}) },
      async (rawUnknown: unknown) => {
        try {
          const data = parseGetSessionPayload(rawUnknown);
          if (isWebExtensionAccountMismatch(data)) {
            const apiTabs = await fetchSessionTabsFromApi(sessionId);
            resolve({ ...apiTabs, accountMismatch: true });
            return;
          }
          const tabData =
            data?.tabData && typeof data.tabData === 'object'
              ? (data.tabData as Record<string, SessionTabData>)
              : {};
          const tabId = Array.isArray(data.tabId) ? data.tabId.map(String) : [];
          resolve({
            tabId,
            tabOrgId: Array.isArray(data.tabOrgId) ? data.tabOrgId : [],
            tabData,
            isWorkSpace: data.isWorkSpace === true,
            accountMismatch: false,
            sleepingTabCount:
              typeof data.sleepingTabCount === 'number'
                ? data.sleepingTabCount
                : undefined,
          });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}
